import { useState } from "react";
import { Link } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import { sortMedia, typeLabelMap } from "@/utils/media";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/feature/SectionHeading";

type RankSort = "heat" | "rating";

export default function HotSection() {
  const [sort, setSort] = useState<RankSort>("heat");
  const ranked = sortMedia(mediaList, sort).slice(0, 8);
  const latestNews = newsList[0];

  return (
    <section className="py-10 md:py-14">
      <SectionHeading
        eyebrow="Ranking"
        title="热门影视推荐"
        description="根据站内热度与评分整理本周榜单，切换排序即可查看不同维度的热门作品。"
        actionLabel="浏览全部分类"
        actionTo="/category/all"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200/80 px-4 py-3">
            <p className="text-xs text-foreground-500">
              榜单收录 <span className="font-medium text-foreground-900">{ranked.length}</span> 部作品
            </p>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setSort("heat")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  sort === "heat"
                    ? "bg-primary-950 font-medium text-background-50"
                    : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                热度榜
              </button>
              <button
                type="button"
                onClick={() => setSort("rating")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  sort === "rating"
                    ? "bg-primary-950 font-medium text-background-50"
                    : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                评分榜
              </button>
            </div>
          </div>

          <ul>
            {ranked.map((item, index) => (
              <li key={item.id} className="border-b border-background-200/60 last:border-b-0">
                <Link
                  to={`/detail/${item.id}`}
                  className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100/70 md:gap-4"
                >
                  <span
                    className={`flex w-6 shrink-0 items-center justify-center font-label text-base font-semibold ${
                      index < 3 ? "text-accent-600" : "text-foreground-400"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} - 热门推荐`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="hidden shrink-0 rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-800 sm:inline">
                        {typeLabelMap[item.type]}
                      </span>
                    </span>
                    <span className="mt-1 block truncate text-[11px] text-foreground-500">
                      {item.year} · {item.region} · {item.genres.join(" / ")}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-foreground-400">
                      主演：{item.cast.join(" / ")}
                    </span>
                  </span>
                  <span className="hidden w-32 shrink-0 md:block">
                    <span className="flex items-center justify-between text-[10px] text-foreground-400">
                      <span>热度</span>
                      <span className="font-label">{item.heat}</span>
                    </span>
                    <span className="mt-1 block h-1 w-full overflow-hidden rounded-full bg-background-200">
                      <span
                        className="block h-full rounded-full bg-accent-500"
                        style={{ width: `${item.heat}%` }}
                      />
                    </span>
                  </span>
                  <span className="flex w-12 shrink-0 flex-col items-end">
                    <span className="flex items-center gap-0.5 font-label text-sm font-semibold text-accent-700">
                      <i className="ri-star-fill text-[10px]" />
                      {item.rating.toFixed(1)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <p className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">
              Library Overview
            </p>
            <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950">资料库概览</h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { label: "收录作品", value: mediaList.length, unit: "部" },
                { label: "电影", value: mediaList.filter((item) => item.type === "movie").length, unit: "部" },
                { label: "剧集", value: mediaList.filter((item) => item.type === "tv").length, unit: "部" },
                { label: "综艺", value: mediaList.filter((item) => item.type === "variety").length, unit: "部" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-md bg-background-100/80 px-3 py-2.5">
                  <p className="text-[11px] text-foreground-500">{stat.label}</p>
                  <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                    {stat.value}
                    <span className="ml-0.5 text-[11px] font-normal text-foreground-500">{stat.unit}</span>
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {["悬疑", "爱情", "治愈", "犯罪", "古装"].map((tag) => (
                <Link
                  key={tag}
                  to={`/category/all?genre=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full bg-accent-100 px-2.5 py-1 text-[11px] text-accent-800 transition-colors hover:bg-accent-200"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-primary-950 p-5">
            <p className="font-label text-[11px] uppercase tracking-[0.2em] text-accent-300">Latest News</p>
            <h3 className="mt-2 font-heading text-base font-semibold text-background-50">资讯快报</h3>
            <div className="mt-3 rounded-md bg-background-50 p-3">
              <NewsCard item={latestNews} variant="compact" />
            </div>
            <Link
              to={`/news/${latestNews.id}`}
              className="mt-3 inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-accent-300 transition-colors hover:text-accent-200"
            >
              阅读完整报道
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}